/**
 * Note editor — spec §5-F6, §8.5.
 *
 * Autosaves 3s after you stop typing, `⌘S` forces it, `⌘⇧T` converts the
 * selection to a task. Serves both `/notes/:id` and `/projects/:key/docs/:id`,
 * which differ only in where "back" goes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft,
  CheckCheck,
  History,
  Loader2,
  Pin,
  PinOff,
  Trash2,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/shared/RichTextEditor';
import { AttachmentSection, useUploadAttachment } from '@/features/tasks';
import { useProjects } from '@/features/projects';
import {
  useConvertToTask,
  useDeleteNote,
  useNote,
  useNoteVersions,
  useRestoreVersion,
  useTogglePin,
  useUpdateNote,
} from './api';

const AUTOSAVE_MS = 3000;

const SCOPE_LABEL: Record<string, string> = {
  personal: 'Personal — only you can see this',
  project: 'Project — visible to project members',
  task: 'Task — follows the task’s visibility',
  meeting: 'Meeting — you and the attendees',
};

function ConvertDialog({
  open,
  onOpenChange,
  selection,
  noteId,
  defaultProjectId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selection: string;
  noteId: string;
  defaultProjectId?: string;
}) {
  const { data: projects = [] } = useProjects();
  const convert = useConvertToTask(noteId);
  const [projectId, setProjectId] = useState(defaultProjectId ?? '');
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setProjectId(defaultProjectId ?? projects[0]?._id ?? '');
  }, [open, defaultProjectId, projects]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="mb-3 text-[16px] font-semibold" data-testid="convert-dialog">
          Convert to task
        </DialogTitle>
        <p className="mb-3 rounded-[6px] bg-[var(--surface-2)] px-2.5 py-2 text-[13px]">{selection}</p>

        <label className="mb-1 block text-[12px] font-medium" htmlFor="convert-project">
          Project
        </label>
        <select
          id="convert-project"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="mb-4 h-8 w-full rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
        >
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.icon} {p.name}
            </option>
          ))}
        </select>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!projectId || convert.isPending}
            onClick={async () => {
              const task = await convert.mutateAsync({ text: selection, projectId });
              toast.success(`Created ${task.key}`, {
                action: { label: 'Open', onClick: () => navigate(`/tasks/${task.key}`) },
              });
              onOpenChange(false);
            }}
          >
            {convert.isPending ? <Loader2 className="animate-spin" /> : <Wand2 />}
            Create task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VersionsDialog({
  open,
  onOpenChange,
  noteId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  noteId: string;
}) {
  const { data: versions = [], isLoading } = useNoteVersions(open ? noteId : undefined);
  const restore = useRestoreVersion(noteId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogTitle className="mb-3 text-[16px] font-semibold">Version history</DialogTitle>
        {isLoading ? (
          <Skeleton className="h-24" />
        ) : versions.length === 0 ? (
          <p className="text-[13px] vt-muted">
            No snapshots yet — one is kept whenever you make a substantial edit.
          </p>
        ) : (
          <ul className="max-h-80 divide-y divide-[var(--border)] overflow-y-auto">
            {versions.map((v) => (
              <li key={v._id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium">v{v.version}</div>
                  <div className="truncate text-[11px] vt-muted">
                    {new Date(v.createdAt).toLocaleString()} · {v.bodyText.slice(0, 60)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="subtle"
                  onClick={async () => {
                    await restore.mutateAsync(v.version);
                    toast.success(`Restored v${v.version}`);
                    onOpenChange(false);
                  }}
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function NoteEditorPage({ inProject = false }: { inProject?: boolean }) {
  const { id, noteId, key } = useParams();
  const activeId = noteId ?? id!;
  const navigate = useNavigate();
  const { data: note, isLoading } = useNote(activeId);
  const update = useUpdateNote(activeId);
  const togglePin = useTogglePin();
  const remove = useDeleteNote();

  const editorRef = useRef<RichTextEditorHandle>(null);
  const uploadAttachment = useUploadAttachment();
  const [title, setTitle] = useState('');
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [selection, setSelection] = useState('');
  const [versionsOpen, setVersionsOpen] = useState(false);
  const pending = useRef<{ title?: string; body?: Record<string, unknown> }>({});

  useEffect(() => {
    if (note) setTitle(note.title);
  }, [note?._id, note?.title]);

  const save = useCallback(async () => {
    if (Object.keys(pending.current).length === 0) return;
    const patch = pending.current;
    pending.current = {};
    await update.mutateAsync(patch);
    setDirty(false);
    setSavedAt(new Date());
  }, [update]);

  // Autosave on idle — the note is never lost to a closed tab.
  useEffect(() => {
    if (!dirty) return;
    const timer = setTimeout(() => void save(), AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [dirty, save]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void save();
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 't') {
        e.preventDefault();
        const text = editorRef.current?.getSelectionText() ?? '';
        if (!text) {
          toast.message('Select the text to turn into a task first');
          return;
        }
        setSelection(text);
        setConvertOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  if (isLoading) {
    return (
      <div className="p-4">
        <Skeleton className="mb-3 h-8 w-1/2" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!note) {
    return <div className="p-6 text-[13px] vt-muted">That note does not exist, or you cannot see it.</div>;
  }

  const backTo = inProject ? `/projects/${key}/docs` : '/notes';

  return (
    <>
      <PageHeader
        title={
          <input
            value={title}
            data-testid="note-title"
            aria-label="Note title"
            onChange={(e) => {
              setTitle(e.target.value);
              pending.current.title = e.target.value;
              setDirty(true);
            }}
            className="w-full bg-transparent text-[20px] font-semibold outline-none"
          />
        }
        subtitle={
          <span className="flex items-center gap-2">
            <Badge tone="neutral">{note.scope}</Badge>
            <span>{SCOPE_LABEL[note.scope]}</span>
            {dirty ? (
              <span className="flex items-center gap-1">
                <Loader2 className="size-3 animate-spin" /> Saving…
              </span>
            ) : savedAt ? (
              <span className="flex items-center gap-1">
                <CheckCheck className="size-3" /> Saved {savedAt.toLocaleTimeString()}
              </span>
            ) : null}
          </span>
        }
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => navigate(backTo)}>
              <ArrowLeft />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const text = editorRef.current?.getSelectionText() ?? '';
                if (!text) {
                  toast.message('Select the text to turn into a task first');
                  return;
                }
                setSelection(text);
                setConvertOpen(true);
              }}
            >
              <Wand2 />
              Convert selection
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setVersionsOpen(true)}>
              <History />
              History
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={note.isPinned ? 'Unpin note' : 'Pin note'}
              onClick={() => togglePin.mutate({ id: note._id, pinned: note.isPinned })}
            >
              {note.isPinned ? <PinOff /> : <Pin />}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Delete note"
              onClick={async () => {
                if (!window.confirm('Delete this note?')) return;
                await remove.mutateAsync(note._id);
                navigate(backTo);
              }}
            >
              <Trash2 />
            </Button>
          </>
        }
      />

      <div className="mx-auto max-w-3xl p-4">
        <RichTextEditor
          handleRef={editorRef}
          value={note.body}
          onPasteImage={async (files) => {
            for (const file of files) {
              try {
                const uploaded = await uploadAttachment.mutateAsync({
                  file,
                  entityType: 'note',
                  entityId: note._id,
                });
                // Insert where the caret is, so a pasted screenshot lands
                // where it was pasted rather than at the end.
                if (uploaded.previewUrl ?? uploaded.thumbUrl) {
                  editorRef.current?.insertImage(uploaded.previewUrl ?? uploaded.thumbUrl!);
                }
              } catch {
                toast.error(`${file.name} could not be uploaded`);
              }
            }
          }}
          data-testid="note-body"
          minHeight={360}
          placeholder="Start writing. Select any line and press ⌘⇧T to turn it into a task."
          onChange={(doc) => {
            pending.current.body = doc;
            setDirty(true);
          }}
        />
        {note.linkedTaskIds.length > 0 && (
          <p className="mt-3 text-[12px] vt-muted">
            {note.linkedTaskIds.length} linked task{note.linkedTaskIds.length === 1 ? '' : 's'}
          </p>
        )}

        {/* V4 F-2 — the founder wanted to keep a spec PDF or a screenshot with
            the note that discusses it, rather than on a task somewhere else. */}
        <div className="mt-5">
          <AttachmentSection entityId={note._id} entityType="note" />
        </div>
      </div>

      <ConvertDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        selection={selection}
        noteId={note._id}
        defaultProjectId={note.projectId}
      />
      <VersionsDialog open={versionsOpen} onOpenChange={setVersionsOpen} noteId={note._id} />
    </>
  );
}
